import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Alert
} from 'reactstrap';
import { updatePlan } from '../../../store/Admin';
import axios from '../../../store/axios';
import { toast } from 'react-toastify';

const PlanEditModal = ({ isOpen, toggle, plan, onUpdate, isAgencyView = false, workspaceId = null, stripeConfigured = false }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    displayName: '',
    description: '',
    price: 0,
    type: 'subscription',
    interval: 'month',
    isActive: true,
    currency: 'USD',
    monthlyCredits: 0,
    planType: 'business',
    popularPlan: false,
    btnText: 'Get Started',
    features: {
      subWorkspaces: 0,
      contacts: 0,
      users: 0,
      emails: 0,
      callingMinutes: 0,
      agentExecutions: 0,
      preBuiltAgents: 0,
      additionalFeatures: [],
      supportType: ''
    },
    creditsConfig: {
      credits: 0,
      bonusCredits: 0
    },
    metadata: {
      pricePerCredit: 0,
      discountPercent: 0,
      isCustom: false,
      minimumCredits: 0,
      order: 0
    }
  });

  useEffect(() => {
    if (plan) {
      setFormData({
        displayName: plan.displayName || '',
        description: plan.description || '',
        price: plan.price || 0,
        type: plan.type || 'subscription',
        interval: plan.interval || 'month',
        isActive: plan.isActive !== undefined ? plan.isActive : true,
        currency: plan.currency || 'USD',
        monthlyCredits: plan.monthlyCredits || 0,
        planType: plan.planType || 'business',
        popularPlan: plan.popularPlan || false,
        btnText: plan.btnText || 'Get Started',
        features: {
          subWorkspaces: plan.features?.subWorkspaces || 0,
          contacts: plan.features?.contacts || 0,
          users: plan.features?.users || 0,
          emails: plan.features?.emails || 0,
          callingMinutes: plan.features?.callingMinutes || 0,
          agentExecutions: plan.features?.agentExecutions || 0,
          preBuiltAgents: plan.features?.preBuiltAgents || 0,
          additionalFeatures: plan.features?.additionalFeatures || [],
          supportType: plan.features?.supportType || ''
        },
        creditsConfig: {
          credits: plan.creditsConfig?.credits || 0,
          bonusCredits: plan.creditsConfig?.bonusCredits || 0
        },
        metadata: {
          pricePerCredit: plan.metadata?.pricePerCredit || 0,
          discountPercent: plan.metadata?.discountPercent || 0,
          isCustom: plan.metadata?.isCustom || false,
          minimumCredits: plan.metadata?.minimumCredits || 0,
          order: plan.metadata?.order || 0
        }
      });
    }
  }, [plan]);

  const handleInputChange = (e, section = null) => {
    const { name, value, type, checked } = e.target;
    const actualValue = type === 'checkbox' ? checked : 
                       type === 'number' ? parseFloat(value) || 0 : value;

    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [name]: actualValue
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: actualValue
      }));
    }
  };

  const handleAdditionalFeatureChange = (index, value) => {
    const newFeatures = [...formData.features.additionalFeatures];
    newFeatures[index] = value;
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        additionalFeatures: newFeatures
      }
    }));
  };

  const addAdditionalFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        additionalFeatures: [...prev.features.additionalFeatures, '']
      }
    }));
  };

  const removeAdditionalFeature = (index) => {
    const newFeatures = formData.features.additionalFeatures.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        additionalFeatures: newFeatures
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isAgencyView) {
        // Handle agency plans
        if (plan?.isNew) {
          // Create new plan
          const response = await axios.post('/payment/plans/agency', {
            ...formData,
            workspaceId: workspaceId,
            isDraft: !stripeConfigured, // Set as draft if Stripe not configured
            stripeRequired: !stripeConfigured
          });

          if (response.data.success) {
            toast.success('Plan created successfully');
            onUpdate();
          }
        } else {
          // Update existing plan
          const response = await axios.put(`/payment/plans/agency/${plan._id}`, formData);

          if (response.data.success) {
            toast.success('Plan updated successfully');
            onUpdate();
          }
        }
      } else {
        // Handle admin plans (existing logic)
        await dispatch(updatePlan({
          id: plan._id,
          ...formData
        }));
        onUpdate();
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error('Failed to save plan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className={`main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal`}>
      <ModalHeader toggle={toggle}>
        <h3>{plan?.isNew ? 'Create New Plan' : `Edit Plan: ${plan?.displayName || plan?.name}`}</h3>
        {isAgencyView && !stripeConfigured && (
          <Alert color="warning" className="mb-0 mt-2">
            <i className="fas fa-info-circle mr-1"></i>
            Plan will be saved as draft. Configure Stripe to activate it.
          </Alert>
        )}
      </ModalHeader>
      <Form className="mt-4" onSubmit={handleSubmit}>
        <ModalBody>
          <Nav tabs>
            <NavItem>
              <NavLink
                className={activeTab === 'basic' ? 'active' : ''}
                onClick={() => setActiveTab('basic')}
                style={{ cursor: 'pointer' }}
              >
                Basic Info
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'features' ? 'active' : ''}
                onClick={() => setActiveTab('features')}
                style={{ cursor: 'pointer' }}
              >
                Features
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'credits' ? 'active' : ''}
                onClick={() => setActiveTab('credits')}
                style={{ cursor: 'pointer' }}
              >
                Credits & Pricing
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'metadata' ? 'active' : ''}
                onClick={() => setActiveTab('metadata')}
                style={{ cursor: 'pointer' }}
              >
                Metadata
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab} className="mt-3">
            {/* Basic Info Tab */}
            <TabPane tabId="basic">
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Display Name</Label>
                    <Input
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleInputChange}
                      placeholder="Enter display name"
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Price</Label>
                    <Input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      required
                    />
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label>Description</Label>
                <Input
                  type="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Enter plan description"
                />
              </FormGroup>

              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label>Type</Label>
                    <Input
                      type="select"
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                    >
                      <option value="subscription">Subscription</option>
                      <option value="credits">Credits</option>
                      <option value="one-time">One-time</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Interval</Label>
                    <Input
                      type="select"
                      name="interval"
                      value={formData.interval}
                      onChange={handleInputChange}
                      disabled={formData.type !== 'subscription'}
                    >
                      <option value="day">Daily</option>
                      <option value="week">Weekly</option>
                      <option value="month">Monthly</option>
                      <option value="year">Yearly</option>
                      <option value="lifetime">Lifetime</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Plan Type</Label>
                    <Input
                      type="select"
                      name="planType"
                      value={formData.planType}
                      onChange={handleInputChange}
                    >
                      <option value="business">Business</option>
                      <option value="agency">Agency</option>
                      <option value="credits">Credits</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Button Text</Label>
                    <Input
                      name="btnText"
                      value={formData.btnText}
                      onChange={handleInputChange}
                      placeholder="e.g., Get Started"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Currency</Label>
                    <Input
                      type="select"
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      {' '}Active
                    </Label>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        name="popularPlan"
                        checked={formData.popularPlan}
                        onChange={handleInputChange}
                      />
                      {' '}Popular Plan
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
            </TabPane>

            {/* Features Tab */}
            <TabPane tabId="features">
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Sub-Workspaces</Label>
                    <Input
                      type="number"
                      name="subWorkspaces"
                      value={formData.features.subWorkspaces}
                      onChange={(e) => handleInputChange(e, 'features')}
                      min="0"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Contacts</Label>
                    <Input
                      type="number"
                      name="contacts"
                      value={formData.features.contacts}
                      onChange={(e) => handleInputChange(e, 'features')}
                      min="0"
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Users</Label>
                    <Input
                      type="number"
                      name="users"
                      value={formData.features.users}
                      onChange={(e) => handleInputChange(e, 'features')}
                      min="0"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Emails</Label>
                    <Input
                      type="number"
                      name="emails"
                      value={formData.features.emails}
                      onChange={(e) => handleInputChange(e, 'features')}
                      min="0"
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Calling Minutes</Label>
                    <Input
                      type="number"
                      name="callingMinutes"
                      value={formData.features.callingMinutes}
                      onChange={(e) => handleInputChange(e, 'features')}
                      min="0"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Agent Executions</Label>
                    <Input
                      type="number"
                      name="agentExecutions"
                      value={formData.features.agentExecutions}
                      onChange={(e) => handleInputChange(e, 'features')}
                      min="0"
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Pre-Built Agents</Label>
                    <Input
                      type="number"
                      name="preBuiltAgents"
                      value={formData.features.preBuiltAgents}
                      onChange={(e) => handleInputChange(e, 'features')}
                      min="0"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Support Type</Label>
                    <Input
                      name="supportType"
                      value={formData.features.supportType}
                      onChange={(e) => handleInputChange(e, 'features')}
                      placeholder="e.g., Email, Priority, 24/7"
                    />
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label>Additional Features</Label>
                {formData.features.additionalFeatures.map((feature, index) => (
                  <div key={index} className="d-flex mb-2">
                    <Input
                      value={feature}
                      onChange={(e) => handleAdditionalFeatureChange(index, e.target.value)}
                      placeholder="Enter feature"
                    />
                    <Button
                      color="danger"
                      size="sm"
                      className="ml-2"
                      onClick={() => removeAdditionalFeature(index)}
                    >
                      <i className="fa fa-trash"></i>
                    </Button>
                  </div>
                ))}
                <button className="btn-style-new" size="sm" onClick={addAdditionalFeature}>
                  + Add Feature
                </button>
              </FormGroup>
            </TabPane>

            {/* Credits & Pricing Tab */}
            <TabPane tabId="credits">
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Monthly Credits</Label>
                    <Input
                      type="number"
                      name="monthlyCredits"
                      value={formData.monthlyCredits}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Price Per Credit</Label>
                    <Input
                      type="number"
                      name="pricePerCredit"
                      value={formData.metadata.pricePerCredit}
                      onChange={(e) => handleInputChange(e, 'metadata')}
                      min="0"
                      step="0.001"
                    />
                  </FormGroup>
                </Col>
              </Row>

              {formData.type === 'credits' && (
                <>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Credits</Label>
                        <Input
                          type="number"
                          name="credits"
                          value={formData.creditsConfig.credits}
                          onChange={(e) => handleInputChange(e, 'creditsConfig')}
                          min="0"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Bonus Credits</Label>
                        <Input
                          type="number"
                          name="bonusCredits"
                          value={formData.creditsConfig.bonusCredits}
                          onChange={(e) => handleInputChange(e, 'creditsConfig')}
                          min="0"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Minimum Credits</Label>
                        <Input
                          type="number"
                          name="minimumCredits"
                          value={formData.metadata.minimumCredits}
                          onChange={(e) => handleInputChange(e, 'metadata')}
                          min="0"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Discount Percent</Label>
                        <Input
                          type="number"
                          name="discountPercent"
                          value={formData.metadata.discountPercent}
                          onChange={(e) => handleInputChange(e, 'metadata')}
                          min="0"
                          max="100"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </>
              )}
            </TabPane>

            {/* Metadata Tab */}
            <TabPane tabId="metadata">
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Display Order</Label>
                    <Input
                      type="number"
                      name="order"
                      value={formData.metadata.order}
                      onChange={(e) => handleInputChange(e, 'metadata')}
                      min="0"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup check className="custom-check-plan">
                    <Label check>
                      <Input
                        type="checkbox"
                        name="isCustom"
                        checked={formData.metadata.isCustom}
                        onChange={(e) => handleInputChange(e, 'metadata')}
                      />
                      {' '}Custom Plan
                    </Label>
                  </FormGroup>
                </Col>
              </Row>

              <Alert color="info" className="mt-3">
                <h6>Plan Information</h6>
                <div className="small">
                  <p className="mb-1"><strong>Plan ID:</strong> {plan?._id}</p>
                  <p className="mb-1"><strong>Name:</strong> {plan?.name}</p>
                  <p className="mb-1"><strong>Created:</strong> {plan?.createdAt ? new Date(plan.createdAt).toLocaleString() : 'N/A'}</p>
                  <p className="mb-1"><strong>Updated:</strong> {plan?.updatedAt ? new Date(plan.updatedAt).toLocaleString() : 'N/A'}</p>
                  {plan?.stripeProductId && (
                    <p className="mb-1"><strong>Stripe Product ID:</strong> {plan.stripeProductId}</p>
                  )}
                  {plan?.stripePriceId && (
                    <p className="mb-0"><strong>Stripe Price ID:</strong> {plan.stripePriceId}</p>
                  )}
                </div>
              </Alert>
            </TabPane>
          </TabContent>
        </ModalBody>
        <ModalFooter>
          <button className="btn-style-border" color="secondary" onClick={toggle}>
            Cancel
          </button>
          <button className="btn-style-new" color="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                Updating...
              </>
            ) : (
              <>
                <i className="fa fa-save mr-2"></i>Update Plan
              </>
            )}
          </button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default PlanEditModal;